import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { OnboardingProvider, useOnboarding } from "@/components/onboarding/OnboardingContext";
import ProgressBar from "@/components/onboarding/ProgressBar";
import SummaryPanel from "@/components/onboarding/SummaryPanel";
import StepIntro from "@/components/onboarding/StepIntro";
import StepDistribution from "@/components/onboarding/StepDistribution";
import StepConfiguration from "@/components/onboarding/StepConfiguration";
import StepCoordonnees from "@/components/onboarding/StepCoordonnees";
import StepSummary from "@/components/onboarding/StepSummary";
import StepSignature from "@/components/onboarding/StepSignature";
import StepPayment from "@/components/onboarding/StepPayment";
import StepConfirmation from "@/components/onboarding/StepConfirmation";
import { ArrowLeft } from "lucide-react";

const OnboardingContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentStep, selectedPackage, setPackage, goToStep, setSignatureStatus } = useOnboarding();

  // Handle DocuSign and Stripe return URLs
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const event = urlParams.get('event');
    const payment = urlParams.get('payment');
    
    // DocuSign return - go to signature step and mark as signed
    if (event === 'signing_complete') {
      goToStep(6); // Signature step
      setSignatureStatus("signed");
      // Clean URL after a short delay to let the component mount
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 100);
    } else if (event === 'decline' || event === 'cancel') {
      goToStep(6); // Stay on signature step
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 100);
    }
    
    // Stripe return - go to payment step
    if (payment === 'success' || payment === 'cancelled') {
      goToStep(7); // Payment step
    }
  }, [location.search, goToStep, setSignatureStatus]);

  // Get selected package from navigation state or URL
  useEffect(() => {
    const state = location.state;
    const urlParams = new URLSearchParams(location.search);
    const packageFromUrl = urlParams.get('package');
    
    if (state?.selectedPackage) {
      setPackage(state.selectedPackage);
    } else if (packageFromUrl) {
      setPackage(packageFromUrl);
    } else if (!selectedPackage) {
      // Default to premium if no package selected
      setPackage("premium");
    }
  }, [location.state, location.search, setPackage, selectedPackage]);

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <StepIntro />;
      case 2:
        return <StepDistribution />;
      case 3:
        return <StepConfiguration />;
      case 4:
        return <StepCoordonnees />;
      case 5:
        return <StepSummary />;
      case 6:
        return <StepSignature />;
      case 7:
        return <StepPayment />;
      case 8:
        return <StepConfirmation />;
      default:
        return <StepIntro />;
    }
  };

  return (
    <div data-testid="onboarding-page" className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#fff8ed] border-b border-black/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            data-testid="btn-back-to-landing"
            className="flex items-center gap-2 font-body text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors"
          >
            <ArrowLeft size={16} />
            Retour au site
          </button>
          <img
            src="https://customer-assets.emergentagent.com/job_fragrance-network/artifacts/kj0kcj9m_Logo%20Le%20Pschitt%20Franc%CC%A7ais.jpeg"
            alt="Le Pschitt Français"
            className="h-12 w-auto object-contain"
          />
          <div className="w-24" /> {/* Spacer for centering */}
        </div>
      </header>

      {/* Progress Bar */}
      {currentStep < 8 && <ProgressBar currentStep={currentStep} totalSteps={8} />}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Step Content */}
          <div className={`${currentStep >= 2 && currentStep < 8 ? "lg:col-span-2" : "lg:col-span-3"}`}>
            {renderStep()}
          </div>

          {/* Summary Panel (visible from step 2 to 7) */}
          {currentStep >= 2 && currentStep < 8 && (
            <div className="lg:col-span-1">
              <SummaryPanel />
            </div>
          )}
        </div>
      </main>

      {/* Mobile padding for fixed summary */}
      {currentStep >= 2 && currentStep < 8 && (
        <div className="lg:hidden h-24" />
      )}
    </div>
  );
};

const OnboardingParfum = () => {
  return (
    <OnboardingProvider>
      <OnboardingContent />
    </OnboardingProvider>
  );
};

export default OnboardingParfum;
